import React from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { TestContext } from "yup";
import { ProductDetailInfo } from "../ProductDetailPageTypeConfig";
import { Validation } from "../../localization/localeValidation";
import ajaxClient, { MaxServerError } from "../../../../js/common/AjaxClient";
import popupAlertService from "../../../../js/common/PopupAlertService";

interface ProductPriceWatchdogFormProperties {
    readonly productDetailInfo: ProductDetailInfo;
    readonly onSubmitted: (formData: ProductPriceWatchdogFormData) => void;
}

interface ProductPriceWatchdogFormContext {
    price: number;
}

const formSchema = yup.object({
    price: yup.number<number, ProductPriceWatchdogFormContext>()
        .typeError("Zadejte prosím částku.")
        .required("Zadejte prosím částku.")
        .test(
            "lowerThanPrice",
            "Zadaná částka musí být nižší, než je aktuální cena zboží.",
            (value, context: TestContext<ProductPriceWatchdogFormContext>) => context.options.context === undefined || value < context.options.context.price,
        ),
    email: yup.string()
        .lowercase()
        .trim()
        .required("Zadejte prosím Váš e-mail.")
        .matches(Validation.EmailRegEx, "Zadejte prosím platný e-mail"),
    agree: yup.boolean()
        .oneOf([true], "Musíte souhlasit se zasíláním obchodní nabídky.")
        .defined(),
});
export type ProductPriceWatchdogFormData = yup.InferType<typeof formSchema>;

const sendPriceWatchdog = async function(productDetailInfo: ProductDetailInfo, data: ProductPriceWatchdogFormData): Promise<void> {
    try {
        await ajaxClient.post("/Product/jsSendPriceWatchdog", {
            watchdogPrice: data.price,
            productId: productDetailInfo.productId,
            email: data.email,
            ctrl1: "",
            ctrl2: "val2",
        });
    } catch (error) {
        if (error instanceof MaxServerError) {
            void popupAlertService.showAlert(error.message);

            return;
        }

        throw error;
    }
};

export default function ProductPriceWatchdogForm({
    productDetailInfo,
    onSubmitted,
}: ProductPriceWatchdogFormProperties): React.JSX.Element {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<ProductPriceWatchdogFormData, ProductPriceWatchdogFormContext>({
        resolver: yupResolver(formSchema),
        context: { price: productDetailInfo.price },
    });
    const onSubmit: SubmitHandler<ProductPriceWatchdogFormData> = async (data: ProductPriceWatchdogFormData): Promise<void> => {
        await sendPriceWatchdog(productDetailInfo, data);
        onSubmitted(data);
    };

    return (
        <div className="modal-body">
            <form onSubmit={(event): void => void handleSubmit(onSubmit)(event)}>
                <div className="price-watchdog-setup">
                    <p className="fakeh3">Dám Vám vědět, jakmile cena klesne pod...</p>
                    <span
                        className="variant-identifier">{productDetailInfo.title}
                    </span>
                    <span> (varianta {productDetailInfo.productId})</span>

                    <div className="shop-body-input-container">
                        <div className="make-offer-input-container">
                            <div className="make-offer-input">
                                <label htmlFor="wp_wishPrice">Při snížení ceny pod</label>
                                <div className="input-container">
                                    <input
                                        type="number" {...register("price")} placeholder="Zadejte částku"
                                        id="wp_wishPrice" />Kč
                                </div>
                                <label htmlFor="wp_wishPrice" className="error">{errors.price?.message}</label>
                            </div>
                        </div>
                        <div className="make-offer-input-container">
                            <div className="make-offer-input">
                                <label htmlFor="wp_email">Vám pošlu e-mailem upozornění na adresu</label>
                                <div className="input-container">
                                    <input
                                        type="text" placeholder="Zadejte Váš e-mail" {...register("email")}
                                        id="wp_email" />
                                </div>
                                <label htmlFor="wp_email" className="error">{errors.email?.message}</label>
                            </div>
                        </div>
                    </div>

                    <div className="form-group shop-body-checkbox">
                        <label htmlFor="wp_service"><input type="checkbox" {...register("agree")} id="wp_service" />Souhlasím
                            se zasíláním obchodní nabídky.
                        </label>
                        <label htmlFor="wp_service" className="error">{errors.agree?.message}</label>
                    </div>
                    <div className="text-right shop-body-button">
                        <button type="submit" className="btn btn-success">Nastavit hlídání</button>
                    </div>
                </div>
            </form>
        </div>
    );
}
